// 受付（依頼主）の純関数。画面・Worker・テストから共用。
// 下払い(charterCost)と支払通行料(tollPay)は依頼主向けに出さない。

const EVIDENCE_EXT = /\.(jpe?g|png|gif|webp|heic|heif|pdf)$/i
const EVIDENCE_MIME = /^(image\/|application\/pdf)/i

export function isEvidenceFile(filename, contentType) {
  const name = String(filename || '')
  const mime = String(contentType || '')
  if (EVIDENCE_EXT.test(name)) return true
  if (mime && EVIDENCE_MIME.test(mime)) return true
  return false
}

// Storage の object key。日本語ファイル名は S3 互換で落ちることがあるので拡張子だけ使う。
export function evidenceStoragePath(reqId, index, filename, now = Date.now()) {
  const ext = (String(filename || '').match(/(\.[a-z0-9]{1,8})$/i) || ['.bin'])[0].toLowerCase()
  return `${reqId}/${now}-${index}${ext}`
}

export function explainEvidenceError(err) {
  const msg = String((err && (err.message || err.error_description || err.error)) || err || '')
  if (/サインイン|サーバ設定|Workers AI|文字を読み取れ|抽出/.test(msg)) return msg
  if (/bucket not found|not found/i.test(msg) && /bucket|request-evidence/i.test(msg)) {
    return '画像用の箱（request-evidence）が未作成です。veil_neb_uke_intake.sql を全文実行してください。'
  }
  if (/bucket not found/i.test(msg)) {
    return '画像用の箱（request-evidence）が未作成です。veil_neb_uke_intake.sql を全文実行してください。'
  }
  if (/row-level security|violates|not allowed|unauthorized|403|401|permission/i.test(msg)) {
    return '画像の保存権限がありません。SQL全文実行と許可アカウントでのログインを確認してください。'
  }
  if (/payload too large|entity too large|too large|maximum size/i.test(msg)) {
    return 'ファイルが大きすぎます（25MBまで）'
  }
  if (/mime|content.?type|invalid file/i.test(msg)) {
    return '写真またはPDFのみ添付できます'
  }
  return msg || 'ファイルの保存に失敗しました'
}

export function statusFromAttachments(attachments) {
  const list = Array.isArray(attachments) ? attachments : []
  return list.some((a) => isEvidenceFile(a && a.filename, a && a.contentType)) ? 'formal' : 'sashi'
}

export function sanitizeForCustomer(doc) {
  const out = { ...(doc || {}) }
  delete out.charterCost
  delete out.tollPay
  delete out.payFixed
  return out
}

export function ingestFromEmail(mail, existingByMessageId) {
  const messageId = String((mail && mail.messageId) || '').trim()
  if (messageId && existingByMessageId && existingByMessageId[messageId]) {
    return { action: 'skip', id: existingByMessageId[messageId].id }
  }
  const attachments = (mail && mail.attachments) || []
  const status = statusFromAttachments(attachments)
  const fromEmail = String((mail && mail.from) || '').trim()
  const doc = {
    source: 'email',
    status,
    messageId,
    fromEmail,
    subject: String((mail && mail.subject) || ''),
    bodyText: String((mail && mail.text) || ''),
    attachments: attachments.map((a) => ({
      filename: (a && a.filename) || '',
      contentType: (a && a.contentType) || '',
      path: (a && a.path) || '',
    })),
    customerName: '',
    fare: 0,
    createdAt: new Date().toISOString(),
  }
  return { action: 'insert', doc }
}

function originOf(req) {
  if (req.origin) return req.origin
  const stops = Array.isArray(req.stops) ? req.stops : []
  const p = stops.find((s) => s && s.role === 'pick')
  return (p && (p.name || [p.pref, p.city, p.addr].filter(Boolean).join(''))) || ''
}
function destOf(req) {
  if (req.dest) return req.dest
  const stops = Array.isArray(req.stops) ? req.stops : []
  const drops = stops.filter((s) => s && s.role === 'drop')
  const d = drops.length ? drops[drops.length - 1] : null
  return (d && (d.name || [d.pref, d.city, d.addr].filter(Boolean).join(''))) || ''
}

export function requestToTrip(req) {
  const r = req || {}
  const partner = r.partnerName || r.partnerFormal || ''
  const driver = r.driverName || ''
  const kind = partner ? 'charter' : 'own'
  const date = r.date || ''
  return {
    id: r.id,
    source: 'request',
    requestId: r.id,
    manageId: r.id,
    date,
    unloadDate: r.unloadDate || date,
    month: date ? String(date).slice(0, 7) : '',
    staff: r.staff || '',
    kind,
    voided: false,
    customerName: r.customerName || '',
    customerFormal: r.customerFormal || r.customerName || '',
    customerCode: r.customerCode || '',
    customerBranchNo: r.customerBranchNo || '',
    origin: originOf(r),
    dest: destOf(r),
    item: r.item || '',
    vehicleClass: r.vehicleClass || '',
    stops: Array.isArray(r.stops) ? r.stops : [],
    fare: Number(r.fare) || 0,
    tollBill: Number(r.tollBill) || 0,
    charterCost: 0,
    tollPay: 0,
    driverName: driver,
    carNo: r.carNo || '',
    vehicleId: r.carNo || '',
    tel: r.tel || '',
    partnerName: kind === 'charter' ? (r.partnerName || '') : '',
    partnerFormal: kind === 'charter' ? (r.partnerFormal || r.partnerName || '') : '',
    carrier: r.carrier || '',
    youshaDriver: r.youshaDriver || '',
    packing: r.packing || '',
    qty: Number(r.qty) || 0,
    weightKg: Number(r.weightKg) || 0,
    volumeM3: Number(r.volumeM3) || 0,
    prime: r.prime || '',
    slipNo: r.slipNo || '',
    cargoNote: r.cargoNote || '',
    memo1: r.memo1 || '',
    memo2: r.memo2 || '',
    billFixed: r.billFixed || '',
    updatedAt: new Date().toISOString(),
  }
}

const CUSTOMER_KEYS = [
  'customerName', 'prime', 'stops', 'vehicleClass', 'item', 'packing', 'qty', 'weightKg', 'volumeM3',
  'slipNo', 'cargoNote', 'fare', 'tollBill', 'billFixed', 'salesDate', 'receiptDate', 'memo1', 'memo2',
  'driverName', 'carNo', 'tel', 'partnerName', 'carrier', 'youshaDriver', 'date', 'unloadDate',
  'attachments',
]

export function listPlaceLabel(s) {
  if (!s) return ''
  const name = String(s.name || '').trim()
  if (name) return name
  return [s.pref, s.city, s.addr, s.bldg].map((x) => String(x == null ? '' : x).trim()).filter(Boolean).join('')
}

export function applyCustomerSubmit(current, patch) {
  const p = patch || {}
  const next = { ...(current || {}) }
  for (const k of CUSTOMER_KEYS) {
    if (p[k] !== undefined) next[k] = p[k]
  }
  delete next.charterCost
  delete next.tollPay
  delete next.payFixed
  if (p.consented || statusFromAttachments(next.attachments) === 'formal') next.status = 'formal'
  else if (!next.status) next.status = 'sashi'
  next.updatedAt = new Date().toISOString()
  return next
}

export function mergeEvidenceAttachments(doc, added) {
  const extra = Array.isArray(added) ? added.filter(Boolean) : []
  const attachments = [...(Array.isArray(doc && doc.attachments) ? doc.attachments : []), ...extra]
  const wasFormal = (doc && doc.status) === 'formal'
  const nowFormal = statusFromAttachments(attachments) === 'formal'
  return {
    ...(doc || {}),
    attachments,
    status: (wasFormal || nowFormal) ? 'formal' : ((doc && doc.status) || 'sashi'),
  }
}

const LIST_KEYS = ['date', 'customerName', 'origin', 'dest', 'vehicleClass', 'item', 'fare', 'carrier', 'driverName', 'carNo']

function cloneStops(doc) {
  return (Array.isArray(doc && doc.stops) ? doc.stops : []).map((s) => ({ ...(s || {}) }))
}

export function applyListPatch(doc, key, value) {
  const next = { ...(doc || {}) }
  if (!LIST_KEYS.includes(key)) return next
  const raw = value == null ? '' : value
  if (key === 'fare') next.fare = Number(raw) || 0
  else next[key] = String(raw)

  if (key === 'customerName' && !next.customerId) next.customerFormal = String(raw)

  if (key === 'date') {
    next.month = next.date ? String(next.date).slice(0, 7) : ''
    const stops = cloneStops(next)
    const pick = stops.find((s) => s.role === 'pick')
    if (pick) pick.date = next.date
    next.stops = stops
    if (!next.unloadDate) next.unloadDate = next.date
  }

  if (key === 'origin') {
    const stops = cloneStops(next)
    let pick = stops.find((s) => s.role === 'pick')
    if (!pick) {
      pick = { role: 'pick', name: '', pref: '', city: '', addr: '', bldg: '', date: next.date || '', time: '', spec: '', contact: '', tel: '', note: '' }
      stops.unshift(pick)
    }
    pick.name = String(raw)
    next.stops = stops
    next.origin = String(raw)
  }

  if (key === 'dest') {
    const stops = cloneStops(next)
    const drops = stops.filter((s) => s.role === 'drop')
    let drop = drops.length ? drops[drops.length - 1] : null
    if (!drop) {
      drop = { role: 'drop', name: '', pref: '', city: '', addr: '', bldg: '', date: next.date || '', time: '', spec: '', contact: '', tel: '', note: '' }
      stops.push(drop)
    }
    drop.name = String(raw)
    next.stops = stops
    next.dest = String(raw)
  }

  if (key === 'carrier') {
    next.carrier = String(raw)
    if (next.partnerName || next.partnerFormal) next.partnerName = String(raw)
  }

  if (key === 'driverName' && next.partnerName) next.youshaDriver = String(raw)

  delete next.charterCost
  delete next.tollPay
  delete next.payFixed
  next.updatedAt = new Date().toISOString()
  return next
}

export function parseExtractJson(text) {
  const s = String(text || '').trim()
  if (!s) return {}
  const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i)
  const raw = fence ? fence[1] : s
  const start = raw.indexOf('{')
  const end = raw.lastIndexOf('}')
  if (start < 0 || end <= start) return {}
  try {
    const obj = JSON.parse(raw.slice(start, end + 1))
    return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {}
  } catch {
    return {}
  }
}

const DRAFT_FIELD_KEYS = [
  'customerName', 'vehicleClass', 'item', 'packing', 'slipNo', 'cargoNote',
  'driverName', 'carNo', 'tel', 'partnerName', 'carrier', 'youshaDriver',
  'date', 'unloadDate', 'memo1', 'memo2',
  'origin', 'originAddr', 'originTime', 'dest', 'destAddr', 'destTime', 'fare', 'tollBill', 'qty', 'weightKg', 'volumeM3', 'stops',
]
const DRAFT_ALIASES = {
  荷主: 'customerName', 依頼主: 'customerName', 発荷主: 'customerName', お客様: 'customerName', 発注者: 'customerName',
  発地: 'origin', 積地: 'origin', 積込地: 'origin', 出荷地: 'origin', 集荷先: 'origin',
  着地: 'dest', 卸地: 'dest', 納品地: 'dest', 配送先: 'dest', 配達先: 'dest',
  品名: 'item', 品目: 'item', 貨物: 'item',
  車種: 'vehicleClass', 車両: 'vehicleClass',
  運賃: 'fare', 運行日: 'date', 配送日: 'date', 積日: 'date',
}

function isDraftShape(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false
  return DRAFT_FIELD_KEYS.some((k) => obj[k] != null && obj[k] !== '')
    || Object.keys(obj).some((k) => DRAFT_ALIASES[k])
}

export function draftFromAiResponse(res) {
  const seen = new Set()
  const walk = (v) => {
    if (v == null || v === '') return {}
    if (typeof v === 'string') return parseExtractJson(v)
    if (typeof v !== 'object' || Array.isArray(v) || seen.has(v)) return {}
    seen.add(v)
    if (isDraftShape(v)) return v
    for (const k of ['response', 'result', 'text', 'output', 'draft']) {
      const inner = walk(v[k])
      if (isDraftShape(inner)) return inner
    }
    return {}
  }
  return walk(res)
}

function labeledValue(text, labels) {
  const lab = labels.map((x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')
  const patterns = [
    new RegExp(`(?:${lab})\\s*[|｜]\\s*([^\\n|]{1,80})`),
    new RegExp(`(?:${lab})[\\s　]*[:：=]\\s*([^\\n|]{1,80})`),
    new RegExp(`(?:${lab})[\\s　]+([^\\n]{1,80})`),
  ]
  for (const re of patterns) {
    const m = String(text || '').match(re)
    if (!m) continue
    const v = String(m[1] || '').replace(/[#*_`]/g, '').replace(/[|｜].*$/, '').trim()
    if (v && !new RegExp(`^(?:${lab})$`).test(v)) return v
  }
  return ''
}

function toIsoDate(s) {
  const m = String(s || '').match(/(20\d{2})[/\-.年](\d{1,2})[/\-.月](\d{1,2})/)
  if (!m) return ''
  return `${m[1]}-${String(m[2]).padStart(2, '0')}-${String(m[3]).padStart(2, '0')}`
}

function toFareNumber(s) {
  const n = Number(String(s || '').replace(/[,，円¥￥\s　]/g, ''))
  return Number.isFinite(n) && n > 0 ? n : ''
}

export function draftFromMarkdown(md) {
  const s = String(md || '')
  const dateRaw = labeledValue(s, ['運行日', '配送日', '積日', '日付']) || s
  const out = {
    customerName: labeledValue(s, ['荷主', '依頼主', '発荷主', '発注者', 'お客様']),
    origin: labeledValue(s, ['発地', '積地', '積込地', '出荷地', '集荷先']),
    dest: labeledValue(s, ['着地', '卸地', '納品地', '配送先', '配達先']),
    date: toIsoDate(dateRaw) || toIsoDate(s),
    vehicleClass: labeledValue(s, ['車種', '車両']),
    item: labeledValue(s, ['品名', '品目', '貨物']),
    packing: labeledValue(s, ['荷姿']),
    fare: toFareNumber(labeledValue(s, ['運賃', '合計額'])),
    qty: toFareNumber(labeledValue(s, ['数量', '個数'])),
    weightKg: toFareNumber(labeledValue(s, ['重量', 'kg'])),
    slipNo: labeledValue(s, ['伝票番号', '依頼番号', '管理番号']),
  }
  if (/パレット積み|パレット降ろし/.test(s) && !out.packing) out.packing = 'パレット'
  for (const k of Object.keys(out)) {
    if (out[k] === '' || out[k] == null) delete out[k]
  }
  return out
}

export function coalesceDraft(primary, fallback) {
  const a = primary && typeof primary === 'object' && !Array.isArray(primary) ? primary : {}
  const b = fallback && typeof fallback === 'object' && !Array.isArray(fallback) ? fallback : {}
  const out = { ...b }
  for (const [k, v] of Object.entries(a)) {
    if (k === 'stops' && Array.isArray(v) && v.length) { out.stops = v; continue }
    if (!isBlank(v)) out[k] = v
  }
  delete out.charterCost
  delete out.tollPay
  return out
}

export function markdownHasFormLabels(md) {
  return /荷主|発注者|依頼主|発地|積地|集荷先|着地|卸地|配達先|運賃|品名|車種|運行日/.test(String(md || ''))
}

export function draftFromFilename(name) {
  const base = String(name || '').replace(/\\/g, '/').split('/').pop().replace(/\.[a-z0-9]+$/i, '')
  const cleaned = base.replace(/【[^】]*】/g, '').replace(/\[[^\]]*\]/g, '')
  const m = cleaned.match(/(20\d{6})(?:[-ー−–](\d{2}))?/)
  if (!m) return {}
  const ymd = m[1]
  const out = { date: `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}` }
  if (m[2]) out.unloadDate = `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${m[2]}`

  // 依頼書ファイル名から発地/着地を補完
  // 例: 久御山ー岩倉_20260818-19.pdf -> origin=久御山, dest=岩倉
  //      貝塚ー海南_20260821-22.pdf  -> origin=貝塚,  dest=海南
  const idx = cleaned.indexOf(ymd)
  if (idx > 0) {
    const places = cleaned.slice(0, idx).replace(/[_\s]+$/g, '')
    const parts = places.split(/[ー−–-]/).map((s) => String(s || '').trim()).filter(Boolean)
    if (parts.length >= 2) {
      out.origin = parts[0]
      out.dest = parts[1]
      out.originCity = parts[0]
      out.destCity = parts[1]
    }
  }
  return out
}

const CITY_TAIL = /^(.+?(?:郡.+?[町村]|市.+?区|市|区|町|村))(.*)$/
const PLACE_PREF = [
  ['久御山', '京都府'],
  ['岩倉', '愛知県'],
  ['安八', '岐阜県'],
  ['貝塚', '大阪府'],
  ['海南', '和歌山県'],
]

function prefForPlace(s) {
  const t = String(s || '')
  const hit = PLACE_PREF.find(([city]) => t.includes(city))
  return hit ? hit[1] : ''
}

export function splitJpAddress(s) {
  const t = String(s || '').replace(/[\s　]+/g, '').trim()
  if (!t) return null
  const m = t.match(/^(北海道|東京都|.+?府|.+?県)(.*)$/)
  if (m) {
    const pref = m[1]
    const rest = m[2]
    if (!rest) return { pref, city: '', addr: '' }
    const m2 = rest.match(CITY_TAIL)
    if (!m2) return { pref, city: rest, addr: '' }
    return { pref, city: m2[1], addr: m2[2] }
  }
  const cityOnly = t.match(CITY_TAIL)
  if (cityOnly) return { pref: '', city: cityOnly[1], addr: cityOnly[2] }
  return null
}

/** 一覧の発着：都道府県でも市区町村でも、入っている方を出す。どちらも無ければ所名や大まかな地名。 */
export function listPlaceParts(s, fallback = '') {
  const pref = String((s && s.pref) || '').trim()
  const city = String((s && s.city) || '').trim()
  if (pref || city) return { pref, city }
  const raw = String((s && s.name) || fallback || '').trim()
  const parsed = splitJpAddress(raw)
  if (parsed && (parsed.pref || parsed.city)) return { pref: parsed.pref || '', city: parsed.city || '' }
  return { pref: '', city: raw }
}

// 抽出結果にファイル名由来の補完を合わせる。住所が読めているときは、
// ファイル名の地名（例: 安八北=店舗名）を市区町村として使わない。
export function coalesceDraftWithFilename(draft, filename) {
  const fromName = draftFromFilename(filename)
  for (const [addrKey, cityKey] of [['originAddr', 'originCity'], ['destAddr', 'destCity']]) {
    const sp = splitJpAddress(String((draft && draft[addrKey]) || ''))
    if (sp && sp.city) delete fromName[cityKey]
  }
  return coalesceDraft(draft, fromName)
}

export function scrubExtractedDraft(src) {
  const out = src && typeof src === 'object' && !Array.isArray(src) ? { ...src } : {}
  const clean = (s) => String(s || '')
    .replace(/【[^】]*】/g, '')
    .replace(/\[依頼書[^\]]*\]/g, '')
    .replace(/依頼書[貫貰]/g, '')
    .replace(/^[_\s　\-ー−–]+|[_\s　]+$/g, '')
    .trim()
  for (const k of ['origin', 'dest', 'customerName', 'item', 'vehicleClass']) {
    if (out[k] == null || out[k] === '') continue
    out[k] = clean(out[k])
    if (!out[k]) delete out[k]
  }
  if (out.customerName && /エースジャパン/.test(out.customerName)) delete out.customerName
  if (out.customerName && /Co\.?,?\s*Ltd|Inc\.|Corporation/i.test(out.customerName) && !/[ぁ-んァ-ン一-龥]/.test(out.customerName)) {
    delete out.customerName
  }
  for (const k of ['origin', 'originAddr', 'dest', 'destAddr']) {
    if (out[k] && isOwnOfficeText(out[k])) delete out[k]
  }
  if (out.vehicleClass && isSchemaLeak(out.vehicleClass)) delete out.vehicleClass
  if (out.item && isSchemaLeak(out.item)) delete out.item
  if (out.fare === 0 || out.fare === '0') delete out.fare
  return out
}

export function largestJpegInPdf(buf, minBytes = 50000) {
  const raw = buf instanceof Uint8Array ? buf : new Uint8Array(buf || [])
  let best = null
  let i = 0
  while (i < raw.length - 1) {
    if (raw[i] !== 0xff || raw[i + 1] !== 0xd8) { i += 1; continue }
    let j = i + 2
    while (j < raw.length - 1 && !(raw[j] === 0xff && raw[j + 1] === 0xd9)) j += 1
    if (j >= raw.length - 1) break
    const slice = raw.subarray(i, j + 2)
    if (!best || slice.length > best.length) best = slice
    i = j + 2
  }
  return best && best.length >= minBytes ? best : null
}

function normalizeExtractedDraft(extracted) {
  const src = extracted && typeof extracted === 'object' && !Array.isArray(extracted) ? { ...extracted } : {}
  for (const [jp, en] of Object.entries(DRAFT_ALIASES)) {
    if (isBlank(src[en]) && !isBlank(src[jp])) src[en] = src[jp]
  }
  return src
}

const DRAFT_TEXT_KEYS = [
  'customerName', 'vehicleClass', 'item', 'packing', 'slipNo', 'cargoNote',
  'driverName', 'carNo', 'tel', 'partnerName', 'carrier', 'youshaDriver',
  'date', 'unloadDate', 'memo1', 'memo2',
]
const DRAFT_NUM_KEYS = ['fare', 'tollBill', 'qty', 'weightKg', 'volumeM3']

function isBlank(v) {
  if (v == null) return true
  if (typeof v === 'number') return !v
  return String(v).trim() === ''
}

function isOwnOfficeText(s) {
  const t = String(s || '').normalize('NFKC').replace(/[\s　]/g, '')
  if (!t) return false
  if (/エースジャパン/.test(t)) return true
  if (/中之島3[-−–]7/.test(t)) return true
  if (/大阪府中之島/.test(t)) return true
  return false
}

function isSchemaLeak(s) {
  const t = String(s || '').trim()
  if (!t) return false
  if (/\brequired type of\b|^\(?Vehicle Type\)?/i.test(t)) return true
  if (/^\([A-Za-z][A-Za-z /]+\)\s*:/.test(t)) return true
  if (/^[A-Za-z][^ぁ-んァ-ン一-龥]{15,}$/.test(t)) return true
  return false
}

function isDraftJunk(v) {
  const s = String(v == null ? '' : v).trim()
  if (!s) return true
  if (isSchemaLeak(s)) return true
  if (isOwnOfficeText(s)) return true
  if (/【[^】]*】|依頼書[貫貰]/.test(s)) return true
  if (/Co\.?,?\s*Ltd|Inc\.|Corporation/i.test(s) && !/[ぁ-んァ-ン一-龥]/.test(s)) return true
  return false
}

function fillStop(cur, patch) {
  const next = { ...(cur || {}) }
  const keys = ['name', 'pref', 'city', 'addr', 'bldg', 'date', 'time', 'spec', 'contact', 'tel', 'note']
  for (const k of keys) {
    if (!isBlank(patch && patch[k]) && isBlank(next[k])) next[k] = String(patch[k]).trim()
  }
  if (patch && patch.role === 'drop') next.role = 'drop'
  else if (patch && patch.role === 'pick') next.role = 'pick'
  return next
}

export function applyExtractedDraft(form, extracted) {
  const src = scrubExtractedDraft(normalizeExtractedDraft(extracted))
  const next = { ...(form || {}) }
  const filled = []
  delete next.charterCost
  delete next.tollPay
  delete next.payFixed

  for (const k of DRAFT_TEXT_KEYS) {
    if (isBlank(src[k]) || !isDraftJunk(next[k])) continue
    next[k] = String(src[k]).trim()
    filled.push(k)
  }
  for (const k of DRAFT_NUM_KEYS) {
    if (src[k] == null || src[k] === '') continue
    const n = Number(String(src[k]).replace(/[,，円¥￥\s　]/g, ''))
    if (!Number.isFinite(n) || n === 0) continue
    if (!isBlank(next[k])) continue
    next[k] = n
    filled.push(k)
  }
  if (next.customerName && isBlank(next.customerFormal)) next.customerFormal = next.customerName

  let stops = Array.isArray(next.stops) ? next.stops.map((s) => ({ ...(s || {}) })) : []
  if (!stops.length) stops = [{ role: 'pick' }, { role: 'drop' }]
  if (Array.isArray(src.stops) && src.stops.length) {
    src.stops.forEach((p, i) => {
      if (!p) return
      if (!stops[i]) stops[i] = { role: p.role === 'drop' ? 'drop' : 'pick' }
      const before = JSON.stringify(stops[i])
      stops[i] = fillStop(stops[i], p)
      if (JSON.stringify(stops[i]) !== before && !filled.includes('stops')) filled.push('stops')
    })
  }
  const pick = stops.find((s) => s.role === 'pick') || stops[0]
  const dropList = stops.filter((s) => s.role === 'drop')
  const drop = dropList.length ? dropList[dropList.length - 1] : null
  const clearOwnOffice = (stop) => {
    if (!stop) return false
    const blob = `${stop.name || ''}${stop.pref || ''}${stop.city || ''}${stop.addr || ''}`
    if (!isOwnOfficeText(blob) && !isOwnOfficeText(stop.name)) return false
    stop.name = ''
    stop.pref = ''
    stop.city = ''
    stop.addr = ''
    return true
  }
  if (clearOwnOffice(pick) && !filled.includes('origin')) filled.push('origin')
  if (clearOwnOffice(drop) && !filled.includes('dest')) filled.push('dest')
  if (pick && isDraftJunk(pick.name)) {
    const fallback = !isBlank(src.origin) ? String(src.origin).trim() : String(src.originCity || '').trim()
    if (fallback) {
      pick.name = fallback
      if (!filled.includes('origin')) filled.push('origin')
    }
  }
  if (drop && isDraftJunk(drop.name)) {
    const fallback = !isBlank(src.dest) ? String(src.dest).trim() : String(src.destCity || '').trim()
    if (fallback) {
      drop.name = fallback
      if (!filled.includes('dest')) filled.push('dest')
    }
  }
  const fillAddr = (stop, addr) => {
    const parts = splitJpAddress(addr)
    if (!stop || !parts) return false
    let changed = false
    if (isBlank(stop.pref) && parts.pref) { stop.pref = parts.pref; changed = true }
    if (isBlank(stop.city) && parts.city) { stop.city = parts.city; changed = true }
    if (isBlank(stop.addr) && parts.addr) { stop.addr = parts.addr; changed = true }
    return changed
  }
  if (fillAddr(pick, src.originAddr || '')) { if (!filled.includes('origin')) filled.push('origin') }
  if (fillAddr(drop, src.destAddr || '')) { if (!filled.includes('dest')) filled.push('dest') }
  if (pick && isBlank(pick.pref) && isBlank(pick.city)) fillAddr(pick, src.origin)
  if (drop && isBlank(drop.pref) && isBlank(drop.city)) fillAddr(drop, src.dest)
  const applyPlaceHint = (stop, hint) => {
    const h = String(hint || '').trim()
    if (!stop || !h) return false
    const city = String(stop.city || '').trim()
    if (city && (city.includes(h) || h.includes(city))) return false
    stop.city = h
    return true
  }
  if (applyPlaceHint(pick, src.originCity)) { if (!filled.includes('origin')) filled.push('origin') }
  if (applyPlaceHint(drop, src.destCity)) { if (!filled.includes('dest')) filled.push('dest') }
  const applyKnownPref = (stop) => {
    if (!stop) return false
    const pref = prefForPlace(stop.city || stop.name || '')
    if (!pref || stop.pref === pref) return false
    stop.pref = pref
    return true
  }
  if (applyKnownPref(pick) && !filled.includes('origin')) filled.push('origin')
  if (applyKnownPref(drop) && !filled.includes('dest')) filled.push('dest')
  if (pick && isBlank(pick.date) && !isBlank(next.date)) pick.date = next.date
  if (drop && isBlank(drop.date) && !isBlank(next.unloadDate)) drop.date = next.unloadDate
  if (pick && isBlank(pick.time) && !isBlank(src.originTime)) {
    pick.time = String(src.originTime).trim()
    if (!filled.includes('origin')) filled.push('origin')
  }
  if (drop && isBlank(drop.time) && !isBlank(src.destTime)) {
    drop.time = String(src.destTime).trim()
    if (!filled.includes('dest')) filled.push('dest')
  }
  if (next.date) next.month = String(next.date).slice(0, 7)
  next.stops = stops
  next.origin = (pick && (pick.name || [pick.pref, pick.city, pick.addr].filter(Boolean).join(''))) || next.origin || ''
  next.dest = (drop && (drop.name || [drop.pref, drop.city, drop.addr].filter(Boolean).join(''))) || next.dest || ''
  return { form: next, filled }
}

function foldParty(s) {
  return String(s || '').normalize('NFKC')
    .replace(/株式会社|有限会社|合同会社|㈱|㈲|\(株\)|（株）|\(有\)|（有）/g, '')
    .replace(/[\s　・･,.\.。]/g, '')
    .toLowerCase()
}

function placeTokens(s) {
  return (String(s || '').normalize('NFKC').match(/[一-龥ァ-ヴーA-Za-z0-9]{2,}/g) || [])
    .map((t) => foldParty(t))
    .filter((t) => t.length >= 2 && !/^(大阪|東京|京都|名古屋|営業所|本社|株式会社)$/.test(t))
}

function tokenOverlap(a, b) {
  const ta = placeTokens(a)
  const tb = placeTokens(b)
  if (!ta.length || !tb.length) return 0
  let n = 0
  for (const t of ta) {
    if (tb.some((x) => x === t || x.includes(t) || t.includes(x))) n += 1
  }
  return n
}

function bigramSet(s) {
  const t = foldParty(s)
  const set = new Set()
  for (let i = 0; i < t.length - 1; i++) set.add(t.slice(i, i + 2))
  return set
}

function bigramSimilarity(a, b) {
  const aa = bigramSet(a)
  const bb = bigramSet(b)
  if (!aa.size || !bb.size) return 0
  let hit = 0
  for (const g of aa) if (bb.has(g)) hit += 1
  return hit / Math.max(aa.size, bb.size)
}

function bigramOverlapCount(a, b) {
  const aa = bigramSet(a)
  const bb = bigramSet(b)
  let hit = 0
  for (const g of aa) if (bb.has(g)) hit += 1
  return hit
}

function charSimilarity(a, b) {
  const aa = new Set(foldParty(a))
  const bb = new Set(foldParty(b))
  if (!aa.size || !bb.size) return 0
  let hit = 0
  for (const ch of aa) if (bb.has(ch)) hit += 1
  return hit / Math.max(aa.size, bb.size)
}

function charOverlapCount(a, b) {
  const aa = new Set(foldParty(a))
  const bb = new Set(foldParty(b))
  let hit = 0
  for (const ch of aa) if (bb.has(ch)) hit += 1
  return hit
}

export function matchMasterParty(name, list) {
  const q = foldParty(name)
  if (!q || q.length < 2 || !Array.isArray(list)) return null
  const scored = []
  for (const c of list) {
    if (!c) continue
    const formal = foldParty(c.formal || '')
    const nm = foldParty(c.name || '')
    let s = 0
    if (formal === q || nm === q) s = 100
    else if (formal && formal.includes(q)) s = 80 + Math.min(formal.length, q.length)
    else if (formal && q.includes(formal)) s = 45 + Math.min(formal.length, q.length)
    else if (nm && nm.includes(q)) s = 60 + Math.min(nm.length, q.length)
    else if (nm && q.includes(nm)) s = 35 + Math.min(nm.length, q.length)
    else if (tokenOverlap(name, `${c.formal || ''} ${c.name || ''}`) >= 2) s = 40
    else {
      const formalSim = bigramSimilarity(name, c.formal || '')
      const nameSim = bigramSimilarity(name, c.name || '')
      const formalChars = charSimilarity(name, c.formal || '')
      const nameChars = charSimilarity(name, c.name || '')
      const sim = Math.max(formalSim, nameSim)
      const chars = Math.max(formalChars, nameChars)
      if (sim >= 0.6 || chars >= 0.72) {
        const dense = Math.max(
          bigramOverlapCount(name, c.formal || '') + charOverlapCount(name, c.formal || ''),
          bigramOverlapCount(name, c.name || '') + charOverlapCount(name, c.name || ''),
        )
        s = 30 + dense
      }
    }
    if (s) scored.push({ c, s })
  }
  scored.sort((a, b) => b.s - a.s)
  if (scored.length >= 2) {
    const top = scored[0]
    const next = scored[1]
    const topKey = foldParty(top.c.formal || top.c.name || '')
    const nextKey = foldParty(next.c.formal || next.c.name || '')
    if (
      topKey && nextKey &&
      nextKey.length >= topKey.length + 2 &&
      nextKey.includes(topKey) &&
      next.s >= top.s - 12
    ) return next.c
  }
  return scored.length ? scored[0].c : null
}

export function matchVehicleClass(raw, types) {
  const s = String(raw || '').trim()
  const list = Array.isArray(types) ? types.filter(Boolean) : []
  if (!s) return ''
  const exact = list.find((t) => t === s || String(t).toLowerCase() === s.toLowerCase())
  if (exact) return exact
  const compact = s.replace(/[\s　]/g, '')
  const hit = list.find((t) => String(t).replace(/[\s　]/g, '') === compact)
  if (hit) return hit
  const m = compact.match(/^(\d+)\s*t/i)
  if (m) {
    const prefix = `${m[1]}t`
    const cand = list.filter((t) => String(t).toLowerCase().startsWith(prefix.toLowerCase()))
    const tw = cand.find((t) => /tw$/i.test(t))
    if (tw) return tw
    if (cand.length) return cand[0]
  }
  if (isSchemaLeak(s)) return ''
  return s
}

export function suggestMissingFromHistory(form, rows) {
  const out = {}
  const list = Array.isArray(rows) ? rows : []
  const scored = list.map((r) => {
    let s = 0
    s += tokenOverlap(form.customerName || form.customerFormal, r.customerName || r.customerFormal)
    s += tokenOverlap(form.origin, r.origin) * 4
    s += tokenOverlap(form.dest, r.dest) * 4
    s += Math.round(Math.max(bigramSimilarity(form.origin, r.origin), charSimilarity(form.origin, r.origin)) * 8)
    s += Math.round(Math.max(bigramSimilarity(form.dest, r.dest), charSimilarity(form.dest, r.dest)) * 8)
    return { r, s }
  }).filter((x) => x.s >= 3).sort((a, b) => b.s - a.s)
  const top = scored.slice(0, 12).map((x) => x.r)
  const mode = (vals) => {
    const c = {}
    for (const v of vals) {
      const k = String(v || '').trim()
      if (!k) continue
      c[k] = (c[k] || 0) + 1
    }
    let best = '', n = 0
    for (const [k, v] of Object.entries(c)) if (v > n) { best = k; n = v }
    return best
  }
  const customer = mode(top.map((r) => r.customerFormal || r.customerName))
  if (customer) out.customerName = customer
  if (!isBlank(form.origin)) {
    let bestOrigin = ''
    let bestSim = 0
    const q = foldParty(form.origin)
    for (const r of top) {
      const cand = String(r.origin || '').trim()
      if (!cand) continue
      let sim = Math.max(bigramSimilarity(form.origin, cand), charSimilarity(form.origin, cand))
      const fc = foldParty(cand)
      if (q && fc.includes(q)) sim = Math.max(sim, 0.8)
      if (sim > bestSim || (sim === bestSim && cand.length > bestOrigin.length)) {
        bestSim = sim
        bestOrigin = cand
      }
    }
    if (bestOrigin && bestSim >= 0.45 && foldParty(bestOrigin) !== q) out.origin = bestOrigin
  } else {
    const v = mode(top.map((r) => r.origin))
    if (v) out.origin = v
  }
  if (isBlank(form.vehicleClass) || isSchemaLeak(form.vehicleClass)) {
    const v = mode(top.map((r) => r.vehicleClass).filter((x) => x && !isSchemaLeak(x)))
    if (v) out.vehicleClass = v
  }
  if (isBlank(form.item) || isSchemaLeak(form.item)) {
    const v = mode(top.map((r) => r.item).filter((x) => x && !isSchemaLeak(x)))
    if (v) out.item = v
  }
  if (isBlank(form.packing)) {
    const v = mode(top.map((r) => r.packing))
    if (v) out.packing = v
  }
  if (isBlank(form.fare)) {
    const v = mode(top.map((r) => r.fare).filter((n) => Number(n) > 0).map(String))
    if (v) out.fare = Number(v)
  }
  return out
}
